/**
 * Mission: the platform apex mission page for `rileconnect.org/mission`
 * (platform mode, no resolved ecosystem tenant). Lays out the five design
 * pillars in extended form and the boundaries of what the platform refuses to
 * be.
 *
 * Sibling apex pages: `Landing` (`/`) and `About` (`/about`). Shared building
 * blocks live in `CitationFooter` (which all three render) and
 * `platformContent`.
 */
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import Hero from '@/components/PlatformHero/PlatformHero';

import CitationFooter from './CitationFooter';
import { DESIGN_PILLARS } from './platformContent';
import './Mission.css';

const META_DESCRIPTION =
    'The mission of RILE Connect: five design pillars (transparency, trust, ' +
    'accessibility, equity, replicability) and the boundaries that define what ' +
    'the platform refuses to be.';

export default function Mission() {
    return (
        <>
            <SEO title="Our Mission" description={META_DESCRIPTION} />

            <Hero
                pageTitle="Our Mission"
                eyebrow="Design pillars and the boundaries of the platform"
                lead={
                    'Five design pillars constrain every product decision on ' +
                    'RILE Connect. They are intentionally restrictive: by ' +
                    'narrowing what the platform tries to be, they make the ' +
                    'work it does take on more durable.'
                }
                primaryCta={{
                    to: '/ecosystems',
                    label: 'Browse Ecosystems',
                }}
                secondaryCta={{
                    to: '/about',
                    label: 'About the Project',
                }}
            />

            <section className="py-5">
                <Container>
                    <Row className="justify-content-center mb-4">
                        <Col xs={12} md={10} lg={8} className="text-center">
                            <h2 className="h2 text-primary mb-3">
                                The five design pillars
                            </h2>
                        </Col>
                    </Row>
                    <Row className="g-4 justify-content-center">
                        {DESIGN_PILLARS.map((pillar) => (
                            <Col xs={12} md={10} lg={8} key={pillar.name}>
                                <div className="border-start border-3 border-warning ps-4 py-2">
                                    <h3 className="h4 text-primary mb-2">
                                        {pillar.name}
                                    </h3>
                                    <p className="mb-0">{pillar.long}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            <section className="py-5 bg-light">
                <Container>
                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <h2 className="h2 text-primary mb-3">
                                What RILE Connect is not
                            </h2>
                            <p>
                                Defining a platform partly means defining what
                                it refuses to be. RILE Connect is explicitly:
                            </p>
                            <ul className="platform-mission-not-list">
                                <li>
                                    <strong>Not a social network.</strong> There
                                    are no public personal profiles, no follower
                                    counts, no popularity mechanics. The unit of
                                    identity that matters is the relationship
                                    between participants and organizations, not
                                    a personal brand.
                                </li>
                                <li>
                                    <strong>
                                        Not a personalized career portal.
                                    </strong>{' '}
                                    The platform does not host résumés and does
                                    not produce opaque individual rankings.
                                    Discovery is transparent: you see why an
                                    organization or mentor surfaced.
                                </li>
                                <li>
                                    <strong>Not a closed system.</strong> There
                                    is no vendor lock-in. The data model, the
                                    source code, and the architecture are open
                                    so the work can be replicated, audited, and
                                    extended.
                                </li>
                            </ul>
                        </Col>
                    </Row>
                </Container>
            </section>

            <section className="py-5">
                <Container>
                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <h2 className="h2 text-primary mb-3">
                                Equity-first analytics
                            </h2>
                            <p>
                                Most platforms measure engagement: who showed
                                up, who clicked, who came back. RILE Connect
                                tracks those signals too, but the more important
                                question for an ecosystem aimed at equity is the
                                inverse: who is missing? Which regions have no
                                representation? Which sectors are absent from
                                the network? Which kinds of participants the
                                ecosystem is meant to serve are not arriving at
                                all?
                            </p>
                            <p className="mb-0">
                                Analytics on RILE Connect are framed around that
                                question. The absences are the data that matters
                                most, because they are what tell the ecosystem
                                where to do its work next.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            <CitationFooter />
        </>
    );
}
